import type { InputFieldProps } from './types'
import type { FC } from 'react'
import clsx from 'clsx'

import XClose from '@sylveraio/untitled-ui-icons-react/build/esm/XClose'

import { Button } from '../Button'
import { Typography } from '../Typography'

const SIZE_TO_BUTTON_SIZE = {
  lg: 'md',
  sm: 'xs',
  xs: 'xxs',
} as const

export const InputField: FC<InputFieldProps> = ({
  error,
  id,
  label,
  onClear,
  preserveErrorSpace = true,
  size = 'lg' as const,
  ...props
}) => {
  const inputClass = clsx(
    'flex-1 min-w-0 rounded-lg border bg-high font-normal outline-0 focus-visible:ring-[3px] placeholder:text-placeholder px-3 focus:border-2 focus:px-[11px]',
    {
      'min-h-8 text-xs': size === 'xs',
      'min-h-9 text-sm': size === 'sm',
      'min-h-12 text-sm': size === 'lg',
    },
    typeof error === 'undefined'
      ? 'border-default focus:border-content-action-default focus-visible:ring-content-action-subtle focus-visible:ring-offset-2 hover:border-default-hover hover:focus:border-content-action-default'
      : 'border-content-alert-default focus:border-content-alert-default focus-visible:ring-content-alert-subtle focus-visible:ring-offset-2 hover:border-content-alert-default-hover hover:focus:border-content-alert-default-hover',
  )

  const clearButtonWrapperClass = clsx('absolute right-0 ', {
    'py-1 pr-1': size === 'xs',
    'py-0.5 pr-0.5': size === 'sm',
    'p-1': size === 'lg',
  })

  return (
    <div className="w-full flex gap-1 min-w-0 flex-col">
      <label htmlFor={id}>
        <Typography
          size={size === 'xs' ? 'xs' : 'sm'}
          weight="medium"
          className="text-subtle"
        >
          {label}
        </Typography>
      </label>
      <div className="relative flex">
        <input id={id} className={inputClass} {...props} />
        {typeof props.value === 'string' &&
          props.value.length > 0 &&
          onClear && (
            <div className={clearButtonWrapperClass}>
              <Button
                iconLeading={<XClose />}
                onClick={onClear}
                size={SIZE_TO_BUTTON_SIZE[size]}
                variant="subtle"
              />
            </div>
          )}
      </div>

      {(error || preserveErrorSpace) && (
        <Typography
          size={size === 'xs' ? 'xs' : 'sm'}
          weight="medium"
          className="!text-content-alert-dark"
        >
          {error || '\u00A0' /* non-breaking space */}
        </Typography>
      )}
    </div>
  )
}
